import { useCallback, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { v1Client } from '@renderer/chat'
import { useAuth } from '@renderer/chat/providers'
import type { components } from '@renderer/chat/types/backend-openapi'
import { useUser } from '@renderer/hooks/useUser'
import { useGatewayConnect } from '@renderer/overlay/hooks/use-gateway-connect'
import { useGatewayChannel } from './useGatewayChannel'

export type ContextGenerationSnapshot = components['schemas']['ContextGenerationSnapshot']
export type ContextGenerationEvent = components['schemas']['ContextGenerationWebSocketPayload']

const CONTEXT_GENERATION_QUERY_KEY = 'context-generation-status'

function toSnapshot(event: ContextGenerationEvent): ContextGenerationSnapshot {
  const { integrations, completedIntegrations, totalIntegrations, liveContext, progressPercent, updatedAt } = event
  return { integrations, completedIntegrations, totalIntegrations, liveContext, progressPercent, updatedAt }
}

// Gateway delivery is not ordered and the HTTP read can land after a socket
// event, so a snapshot only replaces the cached one when it is newer.
function newerSnapshot(
  current: ContextGenerationSnapshot | undefined | null,
  incoming: ContextGenerationSnapshot,
): ContextGenerationSnapshot {
  if (!current) return incoming
  return Date.parse(incoming.updatedAt) >= Date.parse(current.updatedAt) ? incoming : current
}

interface LastEventState {
  userId: string
  event: ContextGenerationEvent
}

/**
 * Per-integration deep research and live context generation progress.
 *
 * The snapshot loads once over HTTP, again on every gateway reconnect, and is
 * otherwise replaced by `context-generation` WebSocket events, which carry the
 * same shape. Consumers that want to react to a completion (the onboarding
 * toast) read `lastEvent`; `firstCompletion` on it separates the first
 * generation from later refreshes. `lastEvent` is every event as it arrives,
 * not timestamp-ordered: a first completion that arrives late must still
 * reach the toast, while the snapshot it carries may already be stale.
 */
export function useContextGenerationProgress() {
  const { getAuthToken } = useAuth()
  const user = useUser()
  const userId = user?.id
  const queryClient = useQueryClient()
  const [lastEventState, setLastEventState] = useState<LastEventState | null>(null)
  const queryKey = [CONTEXT_GENERATION_QUERY_KEY, userId]

  const query = useQuery({
    queryKey,
    enabled: !!userId,
    queryFn: async () => {
      const response = await v1Client.GET('/context-generation/status', {
        headers: { Authorization: `Bearer ${await getAuthToken()}` },
      })
      if (response.error || !response.data) throw new Error('Failed to fetch context generation status')
      return newerSnapshot(queryClient.getQueryData<ContextGenerationSnapshot>(queryKey), response.data.data)
    },
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  })

  const onEvent = useCallback(
    (payload: ContextGenerationEvent) => {
      if (!userId) return
      setLastEventState({ userId, event: payload })
      queryClient.setQueryData<ContextGenerationSnapshot>(queryKey, (current) =>
        newerSnapshot(current, toSnapshot(payload)),
      )
    },
    // queryKey is rebuilt each render; userId is the part that matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, userId],
  )
  useGatewayChannel('context-generation', onEvent)

  // A completion published while the socket was down is never replayed.
  useGatewayConnect(
    async () => {
      await query.refetch()
    },
    { enabled: !!userId },
  )

  return {
    snapshot: query.data ?? null,
    lastEvent: lastEventState && lastEventState.userId === userId ? lastEventState.event : null,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}
